
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  Briefcase, 
  Calendar, 
  Bot, 
  BarChart3, 
  MessageSquare,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { OnboardingStepProps } from './types';

export interface FeatureTourStepProps extends OnboardingStepProps {}

export const FeatureTourStep: React.FC<FeatureTourStepProps> = ({
  onComplete,
  onSkip,
  isLoading = false
}) => {
  const features = [
    {
      icon: Users,
      title: 'Candidate Management',
      description: 'Manage your candidate pipeline with AI-powered screening and matching',
      badge: 'Core'
    },
    {
      icon: Briefcase,
      title: 'Job Management',
      description: 'Create and track job postings with automated candidate suggestions',
      badge: 'Core'
    },
    {
      icon: Bot,
      title: 'AI Agents',
      description: 'Leverage specialized AI agents for screening, emails, and orchestration',
      badge: 'AI-Powered'
    },
    {
      icon: Calendar,
      title: 'Smart Calendar',
      description: 'AI-enhanced calendar management with natural language scheduling',
      badge: 'AI-Powered'
    },
    {
      icon: MessageSquare,
      title: 'Email Processing',
      description: 'Automatically categorize and process incoming emails',
      badge: 'AI-Powered'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Track performance and get insights on your recruitment process',
      badge: 'Analytics'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Welcome to Your AI-Powered Recruiting Platform</h2>
        <p className="text-muted-foreground">
          Here's what you can do with our platform
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {features.map((feature, index) => (
          <Card key={index} className="relative overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <feature.icon className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </div>
                <Badge variant={feature.badge === 'AI-Powered' ? 'default' : 'secondary'} className="text-xs">
                  {feature.badge}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border rounded-lg p-6">
        <div className="text-center space-y-3">
          <Bot className="h-8 w-8 text-blue-600 mx-auto" />
          <h3 className="font-semibold text-gray-900">Ready to Get Started?</h3>
          <p className="text-sm text-gray-600">
            Your AI agents are already set up and ready to help you streamline your recruiting process.
            Click "Continue" to activate your agents and start using the platform.
          </p>
        </div>
      </div>

      <div className="flex gap-3">
        {onSkip && (
          <Button variant="outline" onClick={onSkip} disabled={isLoading}>
            Skip tour
          </Button>
        )}
        <Button onClick={onComplete} disabled={isLoading} className="flex-1">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              Continue to Agent Setup
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
